import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ExternalLink, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { Dial, Sparkline, STATUS_COLOURS } from './Dial';
import { ActionButton } from './ActionButton';
import { fmtValue } from './MetricTile';

function TargetEditor({ tile, onSaved }) {
  const { http } = useAuth();
  const [amber, setAmber] = useState(tile.thresholds?.amber ?? '');
  const [red, setRed] = useState(tile.thresholds?.red ?? '');
  const [busy, setBusy] = useState(false);
  if (!tile.thresholds) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.put('/admin/dashboard/thresholds', { values: { [tile.key]: { amber: Number(amber), red: Number(red) } } });
      toast.success('Targets saved.'); onSaved(data.thresholds[tile.key]);
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.detail) || 'Could not save targets.'); }
    setBusy(false);
  };
  const hb = tile.thresholds.higher_better;
  const cls = 'w-20 px-2 py-1 rounded border text-sm';
  return (
    <div className="rounded-lg p-3 mt-4" style={{ background: 'rgba(0,0,0,0.035)' }} data-testid="ops-targets">
      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--olive)' }}>Traffic-light targets {hb ? '(higher is better)' : '(lower is better)'}</div>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <label className="flex flex-col gap-1"><span style={{ color: STATUS_COLOURS.amber }}>Amber {hb ? 'at or below' : 'from'}</span><input type="number" value={amber} onChange={(e) => setAmber(e.target.value)} className={cls} style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="ops-target-amber" /></label>
        <label className="flex flex-col gap-1"><span style={{ color: STATUS_COLOURS.red }}>Red {hb ? 'at or below' : 'from'}</span><input type="number" value={red} onChange={(e) => setRed(e.target.value)} className={cls} style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="ops-target-red" /></label>
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="ops-target-save">{busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save</button>
      </div>
    </div>
  );
}

export function ZoomDialog({ tile, onClose, onRefresh }) {
  const { http } = useAuth();
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!tile) return;
    setDetail(null); setErr('');
    http.get(`/admin/dashboard/metric/${tile.key}`).then((r) => setDetail(r.data))
      .catch((e) => setErr((e.response && e.response.data && e.response.data.detail) || 'Could not load the detail.'));
  }, [tile, http]);
  if (!tile) return null;
  const colour = STATUS_COLOURS[tile.status] || STATUS_COLOURS.neutral;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ops-zoom">
        <DialogHeader>
          <DialogTitle className="font-title text-2xl flex items-center gap-3" style={{ color: 'var(--maroon)' }}>
            <span className="w-3 h-3 rounded-full" style={{ background: colour }} /> {tile.label}
          </DialogTitle>
          <DialogDescription>{tile.help}</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="flex flex-col items-center gap-2">
            {tile.kind === 'gauge' ? <Dial value={tile.value} status={tile.status} size={220} unit={tile.unit} label={tile.label} testid="ops-zoom-dial" />
              : <div className="font-title leading-none" style={{ color: 'var(--navy)', fontSize: '4.5rem' }} data-testid="ops-zoom-value">{fmtValue(tile)}</div>}
            {tile.previous != null && <div className="text-xs" style={{ color: 'var(--olive)' }}>last week: {tile.kind === 'money' ? `$${Number(tile.previous).toFixed(0)}` : tile.previous}{tile.kind === 'gauge' ? tile.unit : ''}</div>}
            {detail && <Sparkline points={detail.history} colour={colour} />}
          </div>
          <div>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="ops-zoom-actions">
              {tile.actions.map((a) => <ActionButton key={a.key} action={a} onDone={() => { onRefresh(); onClose(); }} />)}
              {tile.link && <Link to={tile.link} onClick={onClose} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-title" style={{ border: '1px solid var(--navy)', color: 'var(--navy)' }} data-testid="ops-zoom-open"><ExternalLink size={13} /> Open page</Link>}
            </div>
            {err && <p className="text-sm" style={{ color: STATUS_COLOURS.red }} data-testid="ops-zoom-error">{err}</p>}
            {!detail && !err && <p className="text-sm inline-flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 size={14} className="animate-spin" /> Loading…</p>}
            {detail && (
              detail.rows.length === 0 ? <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="ops-zoom-empty">Nothing waiting here.</p> : (
                <ul className="divide-y max-h-72 overflow-y-auto rounded border" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="ops-zoom-rows">
                  {detail.rows.map((r, i) => (
                    <li key={i} className="px-3 py-2 text-sm flex justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate" style={{ color: 'var(--ink)' }}>{r.user_id ? <Link to={`/admin/contacts/${r.user_id}`} className="hover:underline" onClick={onClose}>{r.title}</Link> : r.title}</div>
                        {r.sub && <div className="text-xs truncate" style={{ color: 'var(--olive)' }}>{r.sub}</div>}
                      </div>
                      {r.amount != null && <span className="font-title whitespace-nowrap" style={{ color: 'var(--navy)' }}>${Number(r.amount).toFixed(2)}</span>}
                    </li>
                  ))}
                </ul>
              )
            )}
            <TargetEditor tile={tile} onSaved={() => onRefresh()} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
